'use client'

import React, { createContext, useCallback, useContext, useState, useEffect } from 'react'
import { PageSectionSkeleton } from '@/components/loading-skeletons'
import { useRouter } from '@/lib/react-compat/navigation'
import {
  API_PREFIX,
  AUTH_SESSION_EVENT,
  apiJson,
  clearAuthSession,
  getRefreshToken,
  readAuthSession,
  storeAuthSession,
  type AuthUser,
} from '@/lib/api-client'

interface AuthContextType {
  isAuthenticated: boolean
  user: AuthUser | null
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const session = readAuthSession()
    setUser(session?.user ?? null)
    setIsAuthenticated(!!session)
    setLoading(false)

    const syncSession = () => {
      const nextSession = readAuthSession()
      setUser(nextSession?.user ?? null)
      setIsAuthenticated(!!nextSession)
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'auth-token' && event.key !== 'auth-user') return
      syncSession()
    }

    // 其它标签页改动走 storage 事件；本页的自动续期或续期失败走 AUTH_SESSION_EVENT。
    window.addEventListener('storage', handleStorage)
    window.addEventListener(AUTH_SESSION_EVENT, syncSession)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener(AUTH_SESSION_EVENT, syncSession)
    }
  }, [])

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    try {
      const data = await apiJson<{ token?: string; refreshToken?: string; user?: AuthUser }>(
        '/api/auth/login',
        {
          method: 'POST',
          json: { username, password },
        }
      )

      const token = data.token || `token-${Date.now()}`
      const userData = data.user || { username }

      storeAuthSession({ token, user: userData, refreshToken: data.refreshToken })

      setUser(userData)
      setIsAuthenticated(true)

      console.log('用户登录成功:', username)
      return true
    } catch (error) {
      console.error('登录请求失败:', error)
      return false
    }
  }, [])

  const logout = useCallback(() => {
    const refreshToken = getRefreshToken()
    if (refreshToken) {
      // 撤销服务端会话；keepalive 保证随后的整页跳转不会中断请求，失败也不影响本地登出。
      void fetch(`${API_PREFIX}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        keepalive: true,
      }).catch(() => {})
    }
    clearAuthSession()
    setUser(null)
    setIsAuthenticated(false)

    console.log('用户已登出')

    if (typeof window !== 'undefined') {
      window.location.assign('/login')
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        login,
        logout,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth 必须在 AuthProvider 内部使用')
  }
  return context
}

// 路由保护组件
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()
  const router = useRouter()

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      const currentPath =
        typeof window === 'undefined'
          ? ''
          : `${window.location.pathname}${window.location.search}${window.location.hash}`
      const loginUrl = currentPath
        ? `/login?returnUrl=${encodeURIComponent(currentPath)}`
        : '/login'
      router.push(loginUrl)
    }
  }, [isAuthenticated, loading, router])

  if (loading) {
    return <PageSectionSkeleton />
  }

  if (!isAuthenticated) {
    return null
  }

  return <>{children}</>
}
